/**
 * Landing Page - Peru IA
 * Immersive storytelling with scroll-triggered animations
 */
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRobot,
    faComments,
    faShieldHalved,
    faArrowRight,
    faMagnifyingGlass,
    faScaleBalanced,
    faBolt,
    faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { Button, ThemeToggle } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import { SEO } from '@/components/features/SEO';
import { useFadeIn } from '@/hooks';
import { useAuthStore } from '@/stores';
import styles from './Landing.module.css';

export function Landing() {
    const navigate = useNavigate();
    const { isLogged } = useAuthStore();

    const heroFade = useFadeIn(0.1);
    const problemFade = useFadeIn(0.15);
    const stepsFade = useFadeIn(0.1);
    const step1Fade = useFadeIn(0.15);
    const step2Fade = useFadeIn(0.15);
    const step3Fade = useFadeIn(0.15);
    const socialFade = useFadeIn(0.15);
    const ctaFade = useFadeIn(0.15);

    return (
        <div className={styles.page}>
            <SEO
                title="Peru IA - Elecciones 2026 | Vota Informado"
                description="Tu asistente de inteligencia artificial para conocer a fondo las propuestas de todos los candidatos presidenciales del Peru 2026. Informacion imparcial, verificada por el JNE."
                url="https://peruhonesto.com/home"
                keywords={[
                    "elecciones peru 2026",
                    "candidatos presidenciales peru",
                    "voto informado peru",
                    "propuestas electorales 2026",
                    "peru ia",
                    "inteligencia artificial elecciones",
                    "JNE elecciones 2026",
                    "planes de gobierno peru"
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebApplication",
                    "name": "Peru IA",
                    "url": "https://peruhonesto.com",
                    "description": "Asistente de inteligencia artificial para conocer las propuestas de los candidatos a las elecciones de Peru 2026.",
                    "applicationCategory": "UtilitiesApplication",
                    "operatingSystem": "Web",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "PEN"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Peru IA",
                        "url": "https://peruhonesto.com",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://peruhonesto.com/logo.png"
                        }
                    },
                    "inLanguage": "es"
                }}
            />

            {/* Ambient background */}
            <div className={styles.bgGlow} aria-hidden="true" />

            {/* Nav */}
            <nav className={styles.nav}>
                <div className={styles.navInner}>
                    <div className={styles.navBrand}>
                        <img src="/logo.png" alt="Perú IA" className={styles.navLogo} />
                        <span className={styles.navName}>Perú IA</span>
                    </div>
                    <div className={styles.navActions}>
                        <ThemeToggle size="md" />
                        {!isLogged && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(ROUTES.login)}
                            >
                                Iniciar sesión
                            </Button>
                        )}
                    </div>
                </div>
            </nav>

            {/* ========== HERO ========== */}
            <section
                ref={heroFade.ref}
                className={`${styles.hero} ${heroFade.isVisible ? styles.visible : ''}`}
            >
                <div className={styles.heroInner}>
                    <div className={styles.heroBadge}>
                        <FontAwesomeIcon icon={faShieldHalved} />
                        <span>Datos oficiales del JNE</span>
                    </div>

                    <h1 className={styles.heroTitle}>
                        Elige con datos,<br />
                        no con dudas.
                    </h1>

                    <p className={styles.heroSub}>
                        Conversa con una IA entrenada con los planes de gobierno oficiales
                        de los 36 partidos registrados para las elecciones 2026.
                    </p>

                    <div className={styles.heroCtas}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={() => navigate(ROUTES.peruai)}
                            className={styles.heroPrimary}
                        >
                            <FontAwesomeIcon icon={faComments} />
                            Comenzar a preguntar
                            <FontAwesomeIcon icon={faArrowRight} className={styles.ctaArrow} />
                        </Button>
                        <Button
                            variant="secondary"
                            size="lg"
                            onClick={() => navigate(ROUTES.blog)}
                        >
                            Leer el blog
                        </Button>
                    </div>
                </div>

                {/* Floating chat preview */}
                <div className={styles.heroPreview}>
                    <div className={styles.previewWindow}>
                        <div className={styles.previewDots}>
                            <span /><span /><span />
                        </div>
                        <div className={styles.previewChat}>
                            <div className={styles.previewBubbleUser}>
                                ¿Qué propone Acuña para la educación?
                            </div>
                            <div className={styles.previewBubbleAi}>
                                <div className={styles.previewAvatar}>
                                    <FontAwesomeIcon icon={faRobot} />
                                </div>
                                <div className={styles.previewAiText}>
                                    Según su plan de gobierno registrado en el JNE,
                                    las propuestas incluyen inversión del 6% del PBI
                                    en educación y conectividad digital para escuelas rurales...
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== PROBLEM ========== */}
            <section
                ref={problemFade.ref}
                className={`${styles.problem} ${problemFade.isVisible ? styles.visible : ''}`}
            >
                <div className={styles.sectionInner}>
                    <p className={styles.problemLabel}>El problema</p>
                    <h2 className={styles.sectionTitle}>
                        36 partidos. Cientos de candidatos.<br />
                        ¿Quién tiene tiempo de leer todos los planes?
                    </h2>
                    <p className={styles.sectionText}>
                        La información electoral está dispersa en documentos extensos que
                        nadie lee. Sin acceso claro, votamos por carisma, no por propuestas.
                        Eso tiene que cambiar.
                    </p>
                </div>
            </section>

            {/* ========== HOW IT WORKS ========== */}
            <section
                ref={stepsFade.ref}
                className={`${styles.steps} ${stepsFade.isVisible ? styles.visible : ''}`}
            >
                <div className={styles.sectionInner}>
                    <p className={styles.stepsLabel}>Cómo funciona</p>
                    <h2 className={styles.sectionTitle}>
                        De pregunta a respuesta en segundos
                    </h2>

                    <div className={styles.stepsGrid}>
                        <div
                            ref={step1Fade.ref}
                            className={`${styles.step} ${step1Fade.isVisible ? styles.visible : ''}`}
                        >
                            <div className={styles.stepNumber}>01</div>
                            <div className={styles.stepIcon}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </div>
                            <h3 className={styles.stepTitle}>Pregunta</h3>
                            <p className={styles.stepText}>
                                Escribe cualquier duda sobre candidatos, partidos o propuestas
                                en lenguaje natural.
                            </p>
                        </div>

                        <div
                            ref={step2Fade.ref}
                            className={`${styles.step} ${step2Fade.isVisible ? styles.visible : ''}`}
                            style={{ transitionDelay: '0.15s' }}
                        >
                            <div className={styles.stepNumber}>02</div>
                            <div className={styles.stepIcon}>
                                <FontAwesomeIcon icon={faScaleBalanced} />
                            </div>
                            <h3 className={styles.stepTitle}>Analiza</h3>
                            <p className={styles.stepText}>
                                La IA busca en los planes de gobierno oficiales y te devuelve
                                información verificada y sin sesgos.
                            </p>
                        </div>

                        <div
                            ref={step3Fade.ref}
                            className={`${styles.step} ${step3Fade.isVisible ? styles.visible : ''}`}
                            style={{ transitionDelay: '0.3s' }}
                        >
                            <div className={styles.stepNumber}>03</div>
                            <div className={styles.stepIcon}>
                                <FontAwesomeIcon icon={faBolt} />
                            </div>
                            <h3 className={styles.stepTitle}>Decide</h3>
                            <p className={styles.stepText}>
                                Compara propuestas, entiende las diferencias y vota con
                                información real.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== SOCIAL PROOF / STATS ========== */}
            <section
                ref={socialFade.ref}
                className={`${styles.social} ${socialFade.isVisible ? styles.visible : ''}`}
            >
                <div className={styles.sectionInner}>
                    <div className={styles.statsRow}>
                        <div className={styles.stat}>
                            <span className={styles.statNum}>36</span>
                            <span className={styles.statLabel}>Partidos políticos</span>
                        </div>
                        <div className={styles.statDivider} />
                        <div className={styles.stat}>
                            <span className={styles.statNum}>100%</span>
                            <span className={styles.statLabel}>Datos oficiales JNE</span>
                        </div>
                        <div className={styles.statDivider} />
                        <div className={styles.stat}>
                            <span className={styles.statNum}>24/7</span>
                            <span className={styles.statLabel}>Siempre disponible</span>
                        </div>
                    </div>

                    <div className={styles.trustCards}>
                        <div className={styles.trustCard}>
                            <FontAwesomeIcon icon={faShieldHalved} className={styles.trustIcon} />
                            <h3>Fuentes oficiales</h3>
                            <p>Toda respuesta se basa exclusivamente en documentos registrados ante el JNE.</p>
                        </div>
                        <div className={styles.trustCard}>
                            <FontAwesomeIcon icon={faUserShield} className={styles.trustIcon} />
                            <h3>Sin sesgos</h3>
                            <p>No favorecemos ningún candidato. La IA presenta hechos, no opiniones.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== FINAL CTA ========== */}
            <section
                ref={ctaFade.ref}
                className={`${styles.cta} ${ctaFade.isVisible ? styles.visible : ''}`}
            >
                <div className={styles.ctaInner}>
                    <h2 className={styles.ctaTitle}>
                        Tu voto define el futuro del Perú.
                    </h2>
                    <p className={styles.ctaText}>
                        No dejes que otros decidan por ti. Infórmate en minutos
                        con la herramienta que hace la política accesible para todos.
                    </p>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => navigate(ROUTES.peruai)}
                        className={styles.ctaButton}
                    >
                        <FontAwesomeIcon icon={faComments} />
                        Conversar con Perú IA
                        <FontAwesomeIcon icon={faArrowRight} className={styles.ctaArrow} />
                    </Button>
                    <p className={styles.ctaNote}>
                        Gratis. Sin registro. Sin recomendaciones de voto.
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className={styles.footer}>
                <p>Perú IA · Proyecto independiente para promover el voto informado</p>
                <div className={styles.footerLinks}>
                    <a href={ROUTES.about}>Sobre nosotros</a>
                    <a href={ROUTES.blog}>Blog</a>
                    <a href={ROUTES.privacy}>Privacidad</a>
                    <a href={ROUTES.terms}>Términos</a>
                </div>
                <p className={styles.footerMuted}>
                    Esta plataforma no hace recomendaciones de voto. Su objetivo es proporcionar
                    información verificada para que cada ciudadano tome su propia decisión.
                </p>
            </footer>
        </div>
    );
}
